import Game from '../../Game';
import IPlayerData from '../../Player/IPlayerData';
import DateHelper from '../DateHelper';
import { IAdvertisementPlacement } from './Advertisement';
import ISDKDriver from './Drivers/ISDKDriver';
import { LanguageKey } from './Language';
import { setSentryUserID } from '../../Sentry';
import { FBTrackingEvent } from '../Tracking/FBTrackingEvent';
import { IGameState } from '../../State/Game/IGameState';
import { IAPCatalog } from './Drivers/IAPCatalog';
import { IMessagePayload } from '../../State/Game/IEntryPointData';
import { report } from '../../report';
import { AuthPlatform, PurchaseConsumeStatus } from '@softgames/mobile-dev-tools';
import { IAuthReturnValues } from 'State/Game/Action/SignIn';
import { AdDebugMode } from 'State/Game/AdDebugMode';
import { IAPDebugMode } from 'State/Game/IAPDebugMode';

export default class SDKManager {
    public readonly initialized: Promise<void>;

    private readonly driver: ISDKDriver;

    constructor() {
        this.driver = new Game.config.SDKDriver();
        this.initialized = this.driver.initialized;
    }

    public debugResetFBEvents() {
        this.driver.save({ FBEvents: '' });
    }

    public async loadingProgress(progress: number) {
        return this.driver.loadingProgress(progress);
    }

    public async loadingComplete() {
        await this.driver.loadComplete();

        // Set Sentry context once available.
        // setSentryUserID(this.getUserId());
    }

    public async onStart() {
        return this.driver.onStart();
    }

    public async runQuery(query: string, params: any) {
        return this.driver.runQuery(query, params);
    }

    public async save(data: Partial<IPlayerData>) {
        this.debugReportOnProgressRegression(data.stars);
        return this.driver.save(data);
    }

    private debugLastHighestLevelCompleted: number | null = null;
    private debugReportOnProgressRegression(starsStr?: string) {
        if (!starsStr) return;
        let newHighestLevelCompleted: number | null = null;
        try {
            newHighestLevelCompleted = JSON.parse(starsStr).length;
            const type = typeof newHighestLevelCompleted;
            if (type !== 'number') {
                throw new Error(`newHighestLevelCompleted got unexpected type "${type}"`);
            }
        } catch (error) {
            report('Failed to parse stars', {
                error,
                once: true,
                sampleRate: 0.1,
            });
            return;
        }
        if (
            this.debugLastHighestLevelCompleted !== null &&
            !(newHighestLevelCompleted >= this.debugLastHighestLevelCompleted)
        ) {
            report('Save data regress detected', {
                once: true,
                tags: {
                    highestLevelCompletedPrevious: this.debugLastHighestLevelCompleted,
                    highestLevelCompletedCurrent: newHighestLevelCompleted,
                },
            });
        }
        this.debugLastHighestLevelCompleted = newHighestLevelCompleted;
    }

    public async flushSaveData() {
        return this.driver.flushSaveData();
    }

    public async restore() {
        return this.driver.restore();
    }

    public async playWithFriends(text: string, imageBase64: string, payload: IMessagePayload, friendId?: string) {
        return this.driver.playWithFriends(text, imageBase64, payload, friendId);
    }

    public async customUpdate(
        text: string,
        imageBase64: string,
        friendId: string,
        payload: IMessagePayload,
        strategy?: string,
        cta?: string
    ) {
        return this.driver.customUpdate(text, imageBase64, friendId, payload, strategy, cta);
    }

    public async createContext(playerId: string) {
        return this.driver.createContext(playerId);
    }

    public async purchaseProduct(state: IGameState, productId: string) {
        switch (state.perSessionData.iapDebugMode) {
            case IAPDebugMode.ALWAYS_FAIL:
                throw new Error('[DEBUG MODE::ALWAYS_FAIL]: purchase fail');
            case IAPDebugMode.ALWAYS_SUCCEED:
                console.log('[DEBUG MODE::ALWAYS_SUCCEED]: purchase succesful');
                return {
                    developerPayload: '',
                    paymentID: '',
                    productID: productId,
                    purchaseTime: '',
                    purchaseToken: '',
                    signedRequest: '',
                    isConsumed: false,
                };
        }

        return this.driver.purchaseProduct(productId);
    }

    public async resolveUnconsumedPurchases() {
        return this.driver.resolveUnconsumedPurchases();
    }

    public async getPurchases() {
        return this.driver.getPurchases();
    }

    public getContext(): string {
        return this.driver.getContextID();
    }

    public isPlayerInContext(id: string): boolean {
        return this.driver.isPlayerInContext(id);
    }

    public getContextType(): string {
        return this.driver.getContextType();
    }

    public getEntryPointData() {
        return this.driver.getEntryPointData();
    }

    public getAvatar(): string {
        return this.driver.getAvatar();
    }

    public getUserId(): string {
        return this.driver.getUserId();
    }

    public getGBUserId(): string {
        return this.driver.getGBUserId();
    }

    public getUsername(): string {
        return this.driver.getUsername();
    }

    public async fetchCatalog() {
        return this.driver.fetchCatalog();
    }

    public getCatalog(): IAPCatalog {
        return this.driver.getCatalog();
    }

    public async share(text: string, imageBase64: string, payload?: IMessagePayload, intent?: string) {
        return this.driver.share(text, imageBase64, payload, intent);
    }

    public async getFriendScores(leaderboardName: string, instanceId: string) {
        return this.driver.getFriendsScores(leaderboardName, instanceId);
    }

    public async clearPlayerData() {
        return this.driver.clearPlayerData();
    }

    public async displayAdvertisement(state: IGameState, placement: IAdvertisementPlacement, payload?: any) {
        switch (state.perSessionData.adDebugMode) {
            case AdDebugMode.FAIL_IMMEDIATE:
                throw new Error('[DEBUG MODE::FAIL_IMMEDIATE]: ad failed');
            case AdDebugMode.SUCCESS_IMMEDIATE:
                console.log('[DEBUG MODE::SUCCESS_IMMEDIATE]: ad completed');
                return;
        }

        return this.driver.displayAdvertisement(state, placement, payload);
    }

    public async canSubscribeBot() {
        return this.driver.canSubscribeBot();
    }

    public async isSubscribedToBot() {
        return this.driver.isSubscribedToBot();
    }

    public async subscribeBot() {
        return this.driver.subscribeBot();
    }

    /**
     * Platform supports switching to a different game (i.e. cross promo link).
     */
    public canSwitchGame() {
        return this.driver.canSwitchGame();
    }
    public async gameTracking(
        event: FBTrackingEvent,
        value: string | number = null,
        parameters: { [key: string]: string | number } = null
    ) {
        if (window.simulating) return;
        try {
            this.driver.gameTracking(event, value, parameters);
        } catch (error) {
            report('Tracking failed', {
                error,
                sampleRate: 0.1,
                once: true,
                tags: { event },
            });
        }
    }

    public getLanguage(): LanguageKey {
        return this.driver.getLanguage();
    }

    public async levelStart(level: number) {
        return this.driver.levelStart(level);
    }

    public async levelFinish(level: number, score: number, complete: boolean) {
        return this.driver.levelFinish(level, score, complete);
    }

    /**
     * Check for backup. Create a backup if none exists.
     * This backup is one-time for upgrade 1.x.x to 2.0.x
     * @param playerData
     */
    public async prepareMigrationBackup(playerData: IPlayerData) {
        const backup = playerData.data_backup;

        // tslint:disable-next-line:no-console
        console.log('Checking if backup exists...');

        if (backup && typeof backup === 'string') {
            try {
                const data = JSON.parse(backup);
                if (
                    data !== null &&
                    typeof data === 'object' &&
                    data.root !== null &&
                    typeof data.root === 'object' &&
                    data.root.version // Must have a version - this will replace backups from versions prior to v2.1.2.
                ) {
                    // tslint:disable-next-line:no-console
                    console.log('Backup found. Nothing to do.');
                    return;
                }
            } catch (e) {
                // tslint:disable-next-line:no-console
                console.warn('Failed to parse backup JSON.', e);
            }
        }

        // tslint:disable-next-line:no-console
        console.log('No backup found. Creating backup...');

        const newBackupData = JSON.stringify({
            root: playerData,
            backup_data_timestamp: DateHelper.now.toString(),
        });

        await this.driver.save({ data_backup: newBackupData });

        // tslint:disable-next-line:no-console
        console.log('Backup created.');
    }

    public hasIAPSupport(): boolean {
        return this.driver.hasIAPSupport();
    }

    public hasRewardedAdSupport(): boolean {
        return this.driver.hasRewardedAdSupport();
    }

    public hasInterstitialAdSupport(): boolean {
        return this.driver.hasInterstitialAdSupport();
    }

    public handleStateChanged(state: IGameState) { }

    public async switchGame(appID: string, payload: any) {
        return this.driver.switchGame(appID, payload);
    }

    /**
     * Run a query on GB to get all user level score data based on Leaderboard.
     */
    private async getLeaderboardPlayerScores(): Promise<number[]> {
        try {
            const result = await Game.sdk.runQuery(
                `mutation er($route: String!, $args: JSON) {
                executeRuntimeScript(route: $route, args: $args)
            }`,
                { route: 'fetchLeaderboardEntries', args: {} }
            );

            if (!result || !result.data || !result.data.executeRuntimeScript) {
                return;
            }

            // Map the result to get only the scores by level.
            return result.data.executeRuntimeScript.map(
                (data: any) => data.leaderboard.usersEntry && data.leaderboard.usersEntry.score
            );
        } catch (e) {
            return;
        }
    }

    public async chooseFriend() {
        return await this.driver.chooseFriend();
    }

    public async waitForPaymentsEnabled() {
        return this.driver.waitForPaymentsEnabled();
    }

    public async canCreateShortcut() {
        return this.driver.canCreateShortcut();
    }
    public async createShortcut() {
        return this.driver.createShortcut();
    }

    public async getFriends() {
        return this.driver.getFriends();
    }

    public async signIn(platform: AuthPlatform, silent: boolean): Promise<IAuthReturnValues> {
        return this.driver.signIn(platform, silent);
    }

    public async signOut(): Promise<void> {
        return this.driver.signOut();
    }

    public getFacebookAuthToken(): string {
        return this.driver.getFacebookAuthToken();
    }

    public getFacebookAppScopedId(): string {
        return this.driver.getFacebookAppScopedId();
    }

    public isFacebookConnected(): boolean {
        return this.driver.isFacebookConnected();
    }

    public connectToGBC(): Promise<void> | undefined {
        return this.driver.connectToGBC?.();
    }

    public consumePurchase(state: IGameState, product: any): Promise<boolean> {
        switch (state.perSessionData.iapDebugMode) {
            case IAPDebugMode.ALWAYS_FAIL:
                console.log('[DEBUG MODE::ALWAYS_FAIL]: purchase consume fail');
                return Promise.resolve(false);
            case IAPDebugMode.ALWAYS_SUCCEED:
                console.log('[DEBUG MODE::ALWAYS_SUCCEED]: purchase consume succesful');
                return Promise.resolve(true);
        }

        return this.driver.consumePurchase(product);
    }

    public getCDNUrl(): string {
        return this.driver.getCDNUrl();
    }
}
